import {
  ArrowBack,
  KeyboardArrowDown,
  KeyboardArrowUp
} from '@mui/icons-material'
import { Box, IconButton, Stack, Typography } from '@mui/material'
import queryString from 'query-string'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'

import { useAuth } from 'components/AuthProvider'
import Button from 'components/Button'
import { FEE_PLAN_TYPES } from 'components/pages/Review/ReviewPurchase'
import { dashboardPath, myVaultPath } from 'components/Routes'
import SwitchVault from 'components/SwitchVault/SwitchVault'
import TradingPaused from 'components/TradingPaused'
import InputNumber from './InputNumber'

import { REVIEW_ORDER_EXPIRED, TRADING_IS_PAUSED } from 'constants/index'
import useProducts from 'hooks/useProducts'
import { Vault } from 'api/inventory'

import {
  useReviewOrder,
  useMutateUpdateReviewOrderQuantity,
  useMutateUpdateReviewOrderVault,
  useMutateCreateMarketTransaction
} from 'hooks/useTrading'

import ScrollToTop from 'components/layout/ScrollToTop'
import useStyles from './styles'
import { upperFirst } from 'lodash'
import { formatWeightAsGrams } from 'utils/formatWeightAsGrams'

export default function ReviewSell() {
  const navigate = useNavigate()
  const location = useLocation()
  const { search } = location
  const { id: reviewOrderId, productId: productIdParam } =
    queryString.parse(search)
  const { products } = useProducts()
  const product = products.find(p => p.id === Number(productIdParam))

  const { setToast, refreshCurrentUser } = useAuth()
  const { classes } = useStyles()
  const [amount, setAmount] = useState('0')
  const [weight, setWeight] = useState(0)
  const [isWeightInitialized, setIsWeightInitialized] = useState(false)
  const [tradingPaused, setTradingPaused] = useState(false)

  const reviewOrderIdNum = reviewOrderId ? parseInt(`${reviewOrderId}`) : null

  const { data: reviewOrderData, isLoading: loadingReviewOrder } =
    useReviewOrder(reviewOrderIdNum)

  const updateQuantityMutation = useMutateUpdateReviewOrderQuantity()
  const updateVaultMutation = useMutateUpdateReviewOrderVault()
  const createTransactionMutation = useMutateCreateMarketTransaction()

  // Keep a local copy of the last mutation result for fees/total
  const [updatedOrderData, setUpdatedOrderData] = useState<
    typeof reviewOrderData | null
  >(null)

  const loadingUpdateReviewOrderWithVault = updateVaultMutation.isLoading
  const loadingCreateMarketTransaction = createTransactionMutation.isLoading

  const fees = updatedOrderData?.fees ?? reviewOrderData?.fees
  const vault = reviewOrderData?.vault
  const pricePerUnit = reviewOrderData?.pricePerUnit
  // quantityOwned: the REST API returns this at the top-level of the review order
  const quantityOwned = reviewOrderData?.quantityOwned ?? 0

  const productName = useMemo(() => {
    // product name is not in REST ReviewOrder; the sell links pass productId so
    // it can be looked up. Older links without it fall back to the vault name or
    // a generic label.
    return product?.name ?? vault?.name ?? 'Gold'
  }, [product?.name, vault?.name])

  useEffect(() => {
    if (isWeightInitialized || !reviewOrderData) {
      return
    }

    const initialWeight =
      Number(quantityOwned) > 0
        ? Number(reviewOrderData.quantityMg || 0) / 1000
        : 0
    const initialAmount =
      Number(quantityOwned) > 0
        ? Number(reviewOrderData.totalPrice || 0).toFixed(2)
        : '0'

    setWeight(initialWeight)
    setAmount(initialAmount)
    setIsWeightInitialized(true)
  }, [quantityOwned, isWeightInitialized, reviewOrderData])

  useEffect(() => {
    if (!isWeightInitialized || Number(quantityOwned) > 0) {
      return
    }

    if (weight !== 0) {
      setWeight(0)
    }
    if (amount !== '0') {
      setAmount('0')
    }
  }, [quantityOwned, amount, isWeightInitialized, weight])

  useEffect(() => {
    if (!isWeightInitialized) {
      return
    }

    updateQuantityMutation.mutate(
      {
        id: Number(reviewOrderId),
        quantityMg: Math.round(weight * 1000)
      },
      {
        onSuccess: data => setUpdatedOrderData(data),
        onError: (error: unknown) => {
          const err = error as { message?: string; errorStatus?: string }
          if (err?.errorStatus === TRADING_IS_PAUSED) {
            setTradingPaused(true)
          }
          setToast({
            open: true,
            message: err?.message ?? 'An error occurred',
            type: 'error',
            duration: 3000
          })
        }
      }
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWeightInitialized, reviewOrderId, weight])

  const handleChangeVault = async (id: number | null): Promise<Vault> => {
    return new Promise((resolve, reject) => {
      updateVaultMutation.mutate(
        {
          id: Number(reviewOrderId),
          vaultId: id as number
        },
        {
          onSuccess: data => {
            setUpdatedOrderData(data)
            resolve(data.vault as unknown as Vault)
          },
          onError: reject
        }
      )
    })
  }

  const handleChangeAmountInput = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.value === amount) {
      return
    }
    const am = Number(e.target.value)
    const we =
      Number(pricePerUnit) > 0
        ? Math.max(Math.floor(am / Number(pricePerUnit)) / 1000, 0)
        : 0
    setAmount(Number(e.target.value).toFixed(2).toString())
    setWeight(we)
  }

  const handleChangeWeightInput = (e: ChangeEvent<HTMLInputElement>) => {
    const we = Number(e.target.value)
    if (we === weight) {
      return
    }
    const am = calculateAmount(we)
    setWeight(we)
    setAmount(am)
  }

  const increaseWeight = () => {
    const we =
      weight >= Number(quantityOwned) / 1000
        ? Number(quantityOwned) / 1000
        : Number(weight) + 0.001
    handleChangeWeightInput({
      target: { value: String(we) }
    } as ChangeEvent<HTMLInputElement>)
  }

  const decreaseWeight = () => {
    const we = weight <= 0 ? 0 : Math.max(Number(weight) - 0.001, 0)
    handleChangeWeightInput({
      target: { value: String(we) }
    } as ChangeEvent<HTMLInputElement>)
  }

  const onConfirmSell = () => {
    createTransactionMutation.mutate(Number(reviewOrderId), {
      onSuccess: async () => {
        await refreshCurrentUser()
        setToast({
          open: true,
          message: `Good as gold! Market transaction created.`,
          type: 'success',
          duration: 3000
        })
        setTradingPaused(false)
        navigate(myVaultPath(), { replace: true })
      },
      onError: (error: unknown) => {
        const err = error as { message?: string; errorStatus?: string }
        if (err?.errorStatus === TRADING_IS_PAUSED) {
          setTradingPaused(true)
        }
        setToast({
          open: true,
          message: err?.message ?? 'An error occurred',
          type: 'error',
          duration: 3000
        })
        if (err?.errorStatus === REVIEW_ORDER_EXPIRED) {
          navigate(dashboardPath())
        }
      }
    })
  }

  const liveProductPrice = (
    Number(reviewOrderData?.spotPrice || 0) * 1000
  ).toFixed(2)

  const productInVault = Number(vault?.balance || 0).toFixed(2)

  const quantityRemaining = Math.max(
    Number(quantityOwned) / 1000 - Number(weight),
    0
  )

  const productRemaining = Number(
    Number(liveProductPrice) * quantityRemaining
  ).toFixed(2)

  const effectiveFees = updatedOrderData?.fees ?? fees
  const effectiveTotalPrice =
    updatedOrderData?.totalPrice ?? reviewOrderData?.totalPrice ?? 0

  const total = (
    weight <= 0 || Number(quantityOwned) <= 0 ? 0 : Number(effectiveTotalPrice)
  ).toFixed(2)

  const transactionFee = useMemo(() => {
    return Number(
      effectiveFees?.find(fee => fee.label === FEE_PLAN_TYPES.TRANSACTION)
        ?.amount || 0
    ).toFixed(2)
  }, [effectiveFees])

  const isOverWeight = useMemo(() => {
    return weight > Number(quantityOwned) / 1000
  }, [quantityOwned, weight])

  const isConfirmSellDisabled =
    weight <= 0 ||
    loadingReviewOrder ||
    loadingUpdateReviewOrderWithVault ||
    loadingCreateMarketTransaction ||
    isOverWeight

  const unitType = 'mg'

  const calculateAmount = useCallback(
    (we: number) => {
      return we > 0 ? (we * Number(liveProductPrice)).toFixed(2) : '0'
    },
    [liveProductPrice]
  )

  if (tradingPaused) {
    return <TradingPaused />
  }

  return (
    <ScrollToTop>
      <Box className={classes.sell_page}>
        <Box className={classes.header}>
          <IconButton onClick={() => navigate(myVaultPath())}>
            <ArrowBack color='primary' fontSize='large' />
          </IconButton>
          <Stack alignItems='flex-end'>
            <Typography variant='body1' textTransform='uppercase'>
              SALE PRICE LIVE:&nbsp;
              <Typography variant='body1' color='primary.main' component='span'>
                ${liveProductPrice}
              </Typography>
            </Typography>
            <Typography variant='body1' textTransform='uppercase'>
              IN VAULT:&nbsp;
              <Typography variant='body1' color='primary.main' component='span'>
                ${productInVault}
              </Typography>
            </Typography>
          </Stack>
        </Box>

        <Typography variant='body1' pl={2} pt={2}>
          CHOOSE VAULT:
        </Typography>

        <Box className={classes.choose_vault}>
          <SwitchVault handleActive={handleChangeVault} />
        </Box>

        <Box className={classes.choose_vault}>
          <Stack>
            <Typography variant='body1' textTransform='uppercase'>
              BULLION
            </Typography>
            <Typography variant='body1' textTransform='uppercase'>
              {product?.shortDescription ?? '99.99% fine Gold'}
            </Typography>
          </Stack>
          <Stack justifyContent='center' alignItems='flex-end' gap={1}>
            <IconButton onClick={increaseWeight}>
              <KeyboardArrowUp
                color='primary'
                fontSize='medium'
                transform='scale(1.5, 1.8)'
              />
            </IconButton>
            <Stack alignItems='center'>
              <InputNumber
                name='amount'
                value={amount}
                unitType={unitType}
                onChange={handleChangeAmountInput}
              />
              <InputNumber
                name='weight'
                value={weight}
                unitType={unitType}
                onChange={handleChangeWeightInput}
              />
            </Stack>
            <IconButton onClick={decreaseWeight}>
              <KeyboardArrowDown
                color='primary'
                fontSize='medium'
                transform='scale(1.5, 1.8)'
              />
            </IconButton>
          </Stack>
        </Box>

        <Box className={classes.choose_vault}>
          <Stack>
            <Typography variant='body1'>
              {upperFirst(productName)} Remaining
            </Typography>
            <Typography variant='body1'>&nbsp;</Typography>
          </Stack>
          <Stack alignItems='flex-end'>
            <Typography variant='body1'>${productRemaining}</Typography>
            <Typography variant='body1'>
              {formatWeightAsGrams(quantityRemaining * 1000)}
            </Typography>
          </Stack>
        </Box>

        <Box className={classes.choose_vault}>
          <Stack>
            <Typography variant='h5' color='primary.main'>
              TOTAL
            </Typography>
            <Typography variant='body2' color='primary.main'>
              &nbsp;
            </Typography>
            <Typography variant='body2' color='bronze.main'>
              &nbsp;
            </Typography>
          </Stack>
          <Stack textAlign='right'>
            <Typography variant='h5' color='primary.main'>
              ${total}
            </Typography>
            <Typography variant='body2' color='primary.main'>
              Includes transaction fee of 0.5% + $2.5: ${transactionFee}
            </Typography>
            <Typography variant='body2' color='bronze.main'>
              What's this?
            </Typography>
          </Stack>
        </Box>
        <Box className={classes.choose_vault}>
          {isOverWeight && (
            <Typography variant='body1' textAlign='right' color='error'>
              Quantity must be less than or equal to{' '}
              {formatWeightAsGrams(Number(quantityOwned))}
            </Typography>
          )}
        </Box>

        <Button
          variant='outlined'
          className={classes.btnConfirm}
          onClick={onConfirmSell}
          disabled={isConfirmSellDisabled}
          loading={loadingCreateMarketTransaction}
        >
          Confirm Sell
        </Button>
      </Box>
    </ScrollToTop>
  )
}
